import type { ComponentType } from 'react';
import type { RouteObject } from 'react-router-dom';
import { Navigate } from 'react-router-dom';

import { AppShell } from '@/components/layout/app-shell';
import { routes } from '@/config/routes';
import type { RouteHandle } from '@/types';

import { RequireAuth } from './guards/require-auth';

/** D-01: the five map surfaces `<AppShell>` renders full-bleed (see `RouteHandle`). */
const FULL_BLEED: RouteHandle = { fullBleed: true };

const lazy =
  <K extends string>(loader: () => Promise<Record<K, ComponentType>>, key: K) =>
  async () => {
    const m = await loader();
    return { Component: m[key] };
  };

export const protectedRoutes: RouteObject[] = [
  {
    element: <RequireAuth />,
    children: [
      {
        element: <AppShell />,
        children: [
          // 113583 §1a: the Map is the Grower's landing page, not Devices.
          { path: routes.home, element: <Navigate to={routes.map} replace /> },
          {
            path: routes.devices,
            lazy: lazy(() => import('@/pages/devices/devices.page'), 'DevicesPage'),
          },
          {
            path: routes.device(),
            lazy: lazy(
              () => import('@/pages/device-detail/device-detail.page'),
              'DeviceDetailPage',
            ),
          },
          {
            path: routes.map,
            handle: FULL_BLEED,
            lazy: lazy(() => import('@/pages/map/map.page'), 'MapPage'),
          },
          {
            path: routes.fieldCreate,
            handle: FULL_BLEED,
            lazy: lazy(() => import('@/pages/fields/field-mapping.page'), 'FieldMappingPage'),
          },
          // The SAME screen as `fieldCreate` — Field Mapping mode is one page whose only difference
          // is whether it opens empty or on a Field (grilling Q4: a route, so `useBlocker` guards it
          // and the edit view is linkable).
          {
            path: routes.fieldEdit(),
            handle: FULL_BLEED,
            lazy: lazy(() => import('@/pages/fields/field-mapping.page'), 'FieldMappingPage'),
          },
          // D-01: the Import preview is the fourth full-bleed map surface — <AppShell> skips
          // <PageContainer> and floats <TopNav> over the canvas (Figma 1657:789257).
          {
            path: routes.fieldImport,
            handle: FULL_BLEED,
            lazy: lazy(() => import('@/pages/fields/field-import.page'), 'FieldImportPage'),
          },
          // D-01: the Remap Device page is the fifth full-bleed map surface — <AppShell> skips
          // <PageContainer> and floats <TopNav> over the canvas (Figma 2353:986470).
          {
            path: routes.deviceRemap(),
            handle: FULL_BLEED,
            lazy: lazy(() => import('@/pages/device-remap/device-remap.page'), 'DeviceRemapPage'),
          },
          {
            path: routes.subscriptions,
            lazy: lazy(
              () => import('@/pages/subscriptions/subscriptions.page'),
              'SubscriptionsPage',
            ),
          },
          {
            path: routes.subscriptionsRenew,
            lazy: lazy(
              () => import('@/pages/subscriptions/subscription-renew.page'),
              'SubscriptionRenewPage',
            ),
          },
          // Payment Completed (US-115416 § H). In the shell, so TopNav stays (user decision 2026-10-06;
          // Figma 1933:440488 draws the frame without a nav).
          {
            path: routes.subscriptionRenewal(),
            lazy: lazy(
              () => import('@/pages/subscriptions/payment-completed.page'),
              'PaymentCompletedPage',
            ),
          },
          {
            path: routes.subscriptionAutoRenew(),
            lazy: lazy(
              () => import('@/pages/subscriptions/subscription-auto-renew.page'),
              'SubscriptionAutoRenewPage',
            ),
          },
          {
            path: routes.subscriptionsCheckout,
            lazy: lazy(
              () => import('@/pages/subscriptions/subscription-checkout.page'),
              'SubscriptionCheckoutPage',
            ),
          },
          {
            path: routes.support,
            lazy: lazy(() => import('@/pages/support/support.page'), 'SupportPlaceholderPage'),
          },
          {
            path: routes.ticket(),
            lazy: lazy(() => import('@/pages/support/ticket.page'), 'TicketPlaceholderPage'),
          },
          {
            path: routes.notifications,
            lazy: lazy(
              () => import('@/pages/notifications/notification-settings.page'),
              'NotificationSettingsPlaceholderPage',
            ),
          },
          {
            path: routes.warrantyClaims,
            lazy: lazy(
              () => import('@/pages/warranty/warranty-claims.page'),
              'WarrantyClaimsPlaceholderPage',
            ),
          },
          {
            path: routes.warrantyClaim(),
            lazy: lazy(
              () => import('@/pages/warranty/warranty-claim.page'),
              'WarrantyClaimPlaceholderPage',
            ),
          },
          {
            path: routes.profile,
            lazy: lazy(() => import('@/pages/profile/profile.page'), 'ProfilePlaceholderPage'),
          },
          {
            path: routes.associateGrower,
            lazy: lazy(
              () => import('@/pages/profile/associate-grower.page'),
              'AssociateGrowerPlaceholderPage',
            ),
          },
          {
            path: routes.manageAddress,
            lazy: lazy(() => import('@/pages/profile/manage-address.page'), 'ManageAddressPage'),
          },
          {
            path: routes.paymentMethods,
            lazy: lazy(() => import('@/pages/profile/payment-methods.page'), 'PaymentMethodsPage'),
          },
          // US-113440. The Add Card consent line opens this route in a new tab (ticket 06), so the
          // modal and whatever was typed into it survive.
          {
            path: routes.terms,
            lazy: lazy(() => import('@/pages/legal/terms.page'), 'TermsPage'),
          },
        ],
      },
    ],
  },
];
